import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, Row, Col, ProgressBar, Badge } from 'react-bootstrap';
import { Gamepad2, Layers, Award, TrendingUp, Users } from 'lucide-react';
import { useGames } from '../Context/GameContext';
import { useQuestions } from '../Context/QuestionContext';
import { useContest } from '../Context/ContestContext';
import { apiService } from '../Services/apiService';
import ActiveContestsList from '../Components/ActiveContestsList';

const StatCard = ({ title, value, icon: Icon, color, delay, isLive }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
  >
    <Card className="custom-card border-0 h-100">
      <Card.Body className="d-flex align-items-center p-4">
        <div className={`rounded-circle p-3 bg-${color} bg-opacity-10 text-${color} me-3 position-relative`}>
          <Icon size={28} />
          {isLive && (
             <motion.span 
               className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle"
               animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
               transition={{ repeat: Infinity, duration: 2 }}
             />
          )}
        </div>
        <div>
          <h6 className="text-secondary fw-bold text-uppercase small mb-1 d-flex align-items-center gap-2">
            {title}
            {isLive && <span className="badge bg-danger p-1" style={{fontSize: '8px'}}>CANLI</span>}
          </h6>
          <motion.h3 
            key={value}
            initial={isLive ? { scale: 1.2, color: '#dc3545' } : {}}
            animate={isLive ? { scale: 1, color: '#212529' } : {}}
            className="fw-bold text-dark mb-0"
          >
            {value}
          </motion.h3>
        </div>
      </Card.Body>
    </Card>
  </motion.div>
);

const Dashboard = () => {
    const { games } = useGames();
    const { questions } = useQuestions();
    
    const [statsData, setStatsData] = useState({
        totalGames: 0,
        totalQuestions: 0,
        completedContests: 0,
        activeUsers: 0,
        weeklyActivity: { labels: ['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'], data: [0,0,0,0,0,0,0] },
        topGames: [],
        recentGames: []
    });
    const { socket } = useContest();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await apiService.getDashboardStats();
                setStatsData(data);
            } catch (error) {
                console.error("Failed to fetch dashboard stats", error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    useEffect(() => {
        if (!socket) return;

        socket.emit('join_dashboard');

        socket.on('dashboard_stats_update', (newData) => {
            console.log('[Dashboard] Real-time stats update:', newData);
            setStatsData(prev => ({
                ...prev,
                ...newData,
                totalGames: newData.totalGames || prev.totalGames,
                totalQuestions: newData.totalQuestions || prev.totalQuestions
            }));
        });

        return () => {
            socket.off('dashboard_stats_update');
        };
    }, [socket]);

    const stats = [
        { title: 'Toplam Oyun', value: loading ? '...' : (statsData.totalGames || games.length), icon: Gamepad2, color: 'primary' },
        { title: 'Toplam Soru', value: loading ? '...' : (statsData.totalQuestions || questions.length), icon: Layers, color: 'success' },
        { title: 'Tamamlanan', value: loading ? '...' : statsData.completedContests, icon: Award, color: 'warning' },
        { 
          title: 'Aktif Kullanıcı', 
          value: loading ? '...' : statsData.activeUsers, 
          icon: TrendingUp, 
          color: 'danger',
          isLive: true
        },
    ];

    const maxActivity = Math.max(...statsData.weeklyActivity.data, 1);

    return (
        <div className="py-2 pb-5">
            <h2 className="fw-bold text-dark mb-4">Genel Bakış</h2>
            
            <ActiveContestsList />
            
            <Row className="g-4 mb-5">
                {stats.map((stat, idx) => (
                    <Col md={3} key={idx}>
                        <StatCard {...stat} delay={idx * 0.1} />
                    </Col>
                ))}
            </Row>

            <Row className="g-4">
                <Col lg={8}>
                    {/* Weekly Activity Chart */}
                   <Card className="custom-card border-0 mb-4 shadow-sm">
                      <Card.Header className="bg-white border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                         <h5 className="fw-bold mb-0">Haftalık Aktivite</h5>
                         <Badge bg="primary" className="bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2">Son 7 Gün</Badge>
                      </Card.Header>
                      <Card.Body className="p-4 d-flex align-items-end justify-content-between" style={{height: 250}}>
                         {statsData.weeklyActivity.data.map((count, i) => {
                             const heightPercent = maxActivity > 0 ? (count / maxActivity) * 100 : 0;
                             const label = statsData.weeklyActivity.labels ? statsData.weeklyActivity.labels[i] : '';
                             return (
                                <div key={i} className="d-flex flex-column align-items-center justify-content-end gap-2" style={{width: '10%', height: '100%'}}>
                                   {count > 0 && (
                                       <motion.small 
                                         initial={{opacity: 0, scale: 0.5}}
                                         animate={{opacity: 1, scale: 1}}
                                         transition={{delay: (i * 0.1) + 0.5}}
                                         className="text-primary fw-bold"
                                       >
                                         {count}
                                       </motion.small>
                                   )}
                                   <motion.div 
                                      initial={{height: 0}} 
                                      animate={{height: `${heightPercent}%`}} 
                                      transition={{duration: 0.8, delay: i * 0.1, ease: "easeOut"}}
                                      className="w-100 bg-primary rounded-top opacity-75 position-relative bar-hover"
                                      style={{minHeight: 2, background: 'linear-gradient(to top, #6366f1, #818cf8)'}}
                                   >
                                   </motion.div>
                                   <small className="text-muted fw-bold extra-small">{label}</small>
                                </div>
                             );
                         })}
                      </Card.Body>
                   </Card>

                   {/* Recent Contests Table - Şimdilik gizlendi */}
                   {false && (
                   <Card className="custom-card border-0 shadow-sm">
                      <Card.Header className="bg-white border-0 pt-4 px-4">
                         <h5 className="fw-bold mb-0">Son Tamamlanan Yarışmalar</h5>
                      </Card.Header>
                      <Card.Body className="p-0">
                         <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                               <thead className="bg-light">
                                  <tr>
                                     <th className="px-4 py-3 border-0 text-secondary small fw-bold">YARIŞMA</th>
                                     <th className="py-3 border-0 text-secondary small fw-bold">TARİH</th>
                                     <th className="py-3 border-0 text-secondary small fw-bold text-center">KATILIMCI</th>
                                     <th className="py-3 border-0 text-secondary small fw-bold">ŞAMPİYON</th>
                                  </tr>
                               </thead>
                               <tbody>
                                  {statsData.recentGames && statsData.recentGames.length > 0 ? (
                                     statsData.recentGames.map((game, idx) => (
                                        <motion.tr 
                                            key={idx}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.5 + (idx * 0.1) }}
                                        >
                                           <td className="px-4 py-3">
                                              <div className="d-flex align-items-center gap-2">
                                                 <div className="bg-primary bg-opacity-10 text-primary rounded p-2 d-flex align-items-center justify-content-center" style={{width: 32, height: 32}}>
                                                    <Gamepad2 size={16} />
                                                 </div>
                                                 <div>
                                                    <div className="fw-bold text-dark mb-0">{game.title}</div>
                                                    <small className="text-muted font-monospace">#{game.pin}</small>
                                                 </div>
                                              </div>
                                           </td>
                                           <td className="py-3">
                                              <small className="text-secondary fw-medium">
                                                 {game.ended_at ? new Date(game.ended_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                                              </small>
                                           </td>
                                           <td className="py-3 text-center">
                                              <Badge bg="light" className="text-dark border px-2 py-1">
                                                 <Users size={12} className="me-1" /> {game.participantCount}
                                              </Badge>
                                           </td>
                                           <td className="py-3">
                                              <div className="d-flex align-items-center gap-2">
                                                 <Award size={16} className="text-warning" />
                                                 <span className="fw-bold text-dark small text-uppercase ls-1">{game.winner}</span>
                                              </div>
                                           </td>
                                        </motion.tr>
                                     ))
                                  ) : (
                                     <tr>
                                        <td colSpan="4" className="text-center py-5 text-muted">
                                           Henüz tamamlanmış bir yarışma kaydı bulunmuyor.
                                        </td>
                                     </tr>
                                  )}
                               </tbody>
                            </table>
                         </div>
                      </Card.Body>
                   </Card>
                   )}
                </Col>

                <Col lg={4}>
                    <Card className="custom-card border-0 h-100 shadow-sm overflow-hidden">
                        <Card.Header className="bg-white border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                           <h5 className="fw-bold mb-0">En Çok Oynananlar</h5>
                           <TrendingUp size={20} className="text-primary" />
                        </Card.Header>
                        <Card.Body className="p-4">
                           {statsData.topGames && statsData.topGames.length > 0 ? (
                               statsData.topGames.map((game, idx) => {
                                   const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
                                   const color = colors[idx % colors.length];
                                   return (
                                       <motion.div 
                                          className="mb-4" 
                                          key={idx}
                                          initial={{ opacity: 0, y: 20 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ delay: 0.6 + (idx * 0.1) }}
                                       >
                                          <div className="d-flex justify-content-between align-items-center mb-2">
                                             <div className="d-flex align-items-center gap-2 overflow-hidden">
                                                <div 
                                                   className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white small"
                                                   style={{ width: 24, height: 24, backgroundColor: color, flexShrink: 0, fontSize: '10px' }}
                                                >
                                                   {idx + 1}
                                                </div>
                                                {game.id ? (
                                                    <Link to={`/host/game/${game.id}`} className="text-decoration-none text-dark fw-bold text-truncate hover-primary small">
                                                        {game.title}
                                                    </Link>
                                                ) : (
                                                    <span className="fw-bold text-dark text-truncate small">{game.title}</span>
                                                )}
                                             </div>
                                             <div className="text-end">
                                                <span className="fw-bold d-block" style={{ color: color, fontSize: '14px' }}>%{game.percentage}</span>
                                                <small className="text-muted extra-small">{game.count} Oyun</small>
                                             </div>
                                          </div>
                                          <div className="progress rounded-pill overflow-hidden" style={{height: 8, backgroundColor: 'rgba(0,0,0,0.05)'}}>
                                             <motion.div 
                                                className="progress-bar rounded-pill"
                                                initial={{ width: 0 }}
                                                animate={{ width: `${game.percentage}%` }}
                                                transition={{ duration: 1, delay: 0.8 + (idx * 0.1) }}
                                                style={{ backgroundColor: color }}
                                             />
                                          </div>
                                       </motion.div>
                                   );
                               })
                           ) : (
                               <div className="text-center text-muted mt-5 py-5 d-flex flex-column align-items-center gap-3">
                                   <div className="bg-light rounded-circle p-4">
                                      <Gamepad2 size={40} className="opacity-25" />
                                   </div>
                                   <span>Henüz yeterli oyun verisi bulunmuyor.</span>
                               </div>
                           )}

                           <div className="mt-4 p-3 rounded-3 bg-light border border-dashed text-center">
                              <small className="text-secondary fw-medium">Veriler anlık olarak güncellenmektedir.</small>
                           </div>
                        </Card.Body>
                        
                    </Card>
                </Col>
            </Row>
{/* asdf */}
            <style>{`
               .extra-small { font-size: 0.7rem; }
               .ls-1 { letter-spacing: 1px; }
               .bar-hover:hover { opacity: 1 !important; filter: brightness(1.1); }
               .hover-primary:hover { color: #6366f1 !important; }
               .custom-card { border-radius: 16px; overflow: hidden; }
            `}</style>
        </div>
    );
};

export default Dashboard;
